import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { editUserInClub, getClub, getClubMember, joinTheClub } from '../../../service/clubs';
import { getClubGenres } from '../../../service/genres';
import { getClubBookcase, getLastClubBook, getPagesAndBooksCountByClubId } from '../../../service/bookcase';
import { Book } from './Book/Book';
import { getLastestClubMeets } from '../../../service/meet';

import Profile from './Profile/Profile';
import styles from './ClubProfile.module.scss'
import Meet from './Meet/Meet';
import { getFormattedClubName, navigateToClub } from '../../../utils/club';
import { useUserStore } from '../../../store/useUser';
import Button from '../../../components/Button/Button';
import { ClubUser } from '../../../models/clubUser';
import { createErrorNotification } from '../../../service/notification';
import FloatButton from '../../../components/FloatButton/FloatButton';
import { Club } from '../../../models/club';
import { Bookcase } from '../../../models/bookcase';
import { Meet as MeetModel } from '../../../models/meet';

import { Member } from '../../../components/Member/Member';
import useHeader from '../../../hooks/useHeader';
import Modal from '../../../components/Modal/Modal';
import { Form } from 'antd';

const ProfileContext = createContext<{
  clubUser: ClubUser | undefined
  userWasKicked: boolean;
  isAdmin: boolean;
  full: boolean;
  lastBook: Bookcase;
  books: Bookcase[];
  userIsMediator: boolean;
  userIsOut: boolean;
  isMember: boolean;
  isPending: boolean;
  userId: number
  club: Club
  refetch: Function;
  members: ClubUser[];
  membersWasAccepted: ClubUser[];
  lastestMeet: MeetModel;
}>(null);

const SelectActionModal = ({ clubName, clubId }) => {
  const navigate = useNavigate();

  const menu = [
    {
      content: 'Adicionar livros',
      toLink: '/book',
      key: 'meet'
    },
    {
      content: 'Criar novo encontro',
      toLink: navigateToClub(clubName, clubId, '/meet/add'),
      key: 'meet'
    },
    {
      content: 'Criar nova eleição',
      toLink: navigateToClub(clubName, clubId, '/vote/add'),
      key: 'meet'
    },
    {
      content: 'Gerenciar membros',
      toLink: navigateToClub(clubName, clubId, '/members/edit'),
      key: 'members'
    },
    {
      content: 'Gerenciar presenças nos encontros',
      toLink: navigateToClub(clubName, clubId, '/attendances'),
      key: 'attendances'
    }
  ]

  return (
    <Form layout="vertical">
      <h1>Selecione o que você gostaria de fazer.</h1>
      {
        menu.map(menu => (
          <>
            <Button 
              key={menu.key} 
              className={styles.selectionActionButton} 
              type="primary" 
              onClick={()=> navigate(menu.toLink)}>
              <h1>{menu.content || '+'}</h1>
            </Button>
          </>
        )
      )}
    </Form>
  )
}

const ClubProfile: React.FC = () => {
  const navigate = useNavigate();

  const userId = useUserStore(state => state.id)
  const profile = useUserStore(state => state.profile)
  
  const { id, name: clubName } = useParams();
  const clubId = Number(id);
  
  const {data:club, isFetching} = useQuery(
    ['club', clubId],
    () => getClub(clubId),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  const {data:members, refetch} = useQuery(
    ['members', 'club', clubId],
    () => getClubMember(clubId),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  const {data:clubGenres, isFetching: isFetchingGenres} = useQuery(
    ['genres', 'club', clubId],
    () => getClubGenres(clubId),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  const {data:pagesAndBooks} = useQuery(
    ['pagesAndBooks', 'club', clubId],
    () => getPagesAndBooksCountByClubId(clubId),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  const { data:lastBook } = useQuery(
    ['lastBook', 'club', clubId],
    () => getLastClubBook(clubId),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );
  
  const { data:books } = useQuery(
    ['book', 'club', clubId],
    () => getClubBookcase(clubId, ''),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  const { data:lastestMeet } = useQuery(
    ['meets', 'club', clubId],
    () => getLastestClubMeets(clubId),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  useHeader({ header: getFormattedClubName(), subheader: 'Perfil' });

  const onDownload = async (link) => {
    if (!link)
      return createErrorNotification('Não foi possível fazer o download');

    window.open(link, '_blank', 'noopener noreferrer');
  }

  const clubUser = useMemo(()=> userId && members?.find(member => member.userId === userId), [members, userId])
  const userWasKicked = !!clubUser?.isKickedOut
  const userIsOut = !!clubUser?.outClub

  const isMember = !!clubUser && !!clubUser?.wasAccepted && !userIsOut && !userWasKicked
  const isPending = !!clubUser && !clubUser?.wasAccepted && !userIsOut && !userWasKicked
  
  const isAdmin = !!clubUser?.isCreator && clubUser?.userId === userId
  const userIsMediator = !!clubUser?.isMediator && !clubUser?.outClub
  const membersWasAccepted = members?.filter(member => member.wasAccepted)
  const full = membersWasAccepted?.length === club?.limitUsers

  return (
    <ProfileContext.Provider 
      value={{ lastestMeet, membersWasAccepted, userWasKicked, userIsOut, books, lastBook, isPending, isMember, isAdmin, userIsMediator, full, clubUser, members, club, refetch, userId }}>
        <main className={styles.clubContainer}>
          <div>
            <Profile 
              navigate={navigate}
              description={club?.description || ''} 
              name={club?.name || ''}
              pathPhoto={club?.pathPhoto || ''}
              genres={clubGenres || []}
              booksReaded={pagesAndBooks?.books || 0}
              pagesReaded={pagesAndBooks?.pages || 0}
              members={membersWasAccepted?.length || 0}
              />
          </div>

          {userId && 
            <div className={styles.rulesAndJoinActions}>
              {club?.doc && <Button onClick={()=>onDownload(club?.doc)} style={styles.buttonRules}>Regras</Button>}
              <ButtonJoin />
            </div>
          }

          {(!!club?.isPrivate && !isMember) ? 
              <span>Esse clube é privado, peça pra fazer parte!<br/></span>
            :
          <>
          <MemberSection/>
          <LastBookSection/>
          <BooksSection/>
          <br/>
          <LastMeet/>
          <Modal
            renderToggle={({ toggleModal }) =>
              (profile === 'Criador' && isAdmin)
                && (
                <FloatButton
                  onClick={toggleModal}
                />
              )
            }
          >
            {()=> <SelectActionModal clubId={clubId} clubName={clubName} />}
          </Modal>
        </>}
      </main>
    </ProfileContext.Provider>
  )
}

const LastMeet = () => {
  const { lastestMeet, club } = useContext(ProfileContext);

  if(!lastestMeet) return <></>

  return (
    <NavLink to={navigateToClub(club?.name,club?.id, '/meets')}>
      <br/>
      <section>
        <header>
          <h1><strong>Próximo Encontro</strong></h1>
      </header>
      <div className={styles.meets}>
        <Meet
          meet={lastestMeet}
          key={`lastestMeet-${lastestMeet?.id}`}
        />
      </div>
    </section>
  </NavLink>
  )
}
    
const LastBookSection = () => {
  const { lastBook } = useContext(ProfileContext);

  if(!lastBook) return <></>

  return (
    <>
      <br/>
      <section className={styles.section}>
        <h1><strong>Livro da vez</strong></h1>
        <Book
          bookId={lastBook?.bookId}
          key={`lastBook-${lastBook?.bookId}`}
        />
      </section>
    </>
  )
}

const BooksSection = () => {
  const { books } = useContext(ProfileContext);

  if(!books?.length) return <></>

  return(
    <>
      <br/>
      <section>
        <h1><strong>Estante</strong></h1>
        <div className={styles.books}>
          {books?.map(book => 
            <Book
              bookId={book?.bookId}
              key={`book-${book?.bookId}`}
            />
          )}
        </div>
      </section>
    </>
  )
}


const MemberSection = () => {
  const { membersWasAccepted } = useContext(ProfileContext);

  if (!membersWasAccepted) return <></>

  return (
    <>
      <br/> 
      <section>
        <header className={styles.header}>
          <h1><strong>Membros</strong></h1>
          <h1><strong>{membersWasAccepted?.length}</strong></h1>
        </header>
        <ul className={styles.memberList}>
          {
            membersWasAccepted?.filter(member => !member.outClub && !member.isKickedOut)
              .map(member =>
              <li key={`member-${member.id}`}>
                <Member
                  editable={false}
                  key={member.id}
                  member={member}
                />
              </li>
            )
          }
        </ul>
      </section>
    </>
  )
}

const ButtonJoin = () => {
  const { userWasKicked, userIsOut, isPending, isMember, isAdmin, userIsMediator, full, userId, club, refetch } = useContext(ProfileContext);

  if (!userId || !club) return <></>

  const handleUserLeavesOrJoinTheClub = async () => {
    if (!!userWasKicked || !!isAdmin) return

    if (!!isPending) return

    if (!!isMember){
      const { error, success } = await editUserInClub(userId, club?.id, 'userLeavesTheClub')

      if (error) return createErrorNotification("Erro, tente novamente agora ou mais tarde!")

      return await refetch();
    }

    if (userIsOut){
      const { error, success } = await editUserInClub(userId, club?.id, 'joinAgain')

      if (error) return createErrorNotification("Erro, tente novamente agora ou mais tarde!")

      return await refetch();
    }

    const { error, success } = await joinTheClub(userId, club?.id)

    if (error) return createErrorNotification("Erro, tente novamente agora ou mais tarde!")

    return await refetch();
  }

  const disabled = !!userWasKicked || !!isAdmin || full || isPending

  return(
    <Button disabled={disabled} style={styles.buttonJoin}
            onClick={handleUserLeavesOrJoinTheClub}> 
      {
        full ? "Clube está cheio" :
        userWasKicked ? "Expulso" :
        isAdmin ? "Criador" :
        userIsMediator ? "Mediador" :
        isMember ? "Participando" :
        isPending ? "Aguarde, seu pedido ainda não foi aceito"
        : "Participar"} 
    </Button>
  )
}

export default ClubProfile;